import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux/es/exports";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
    getWorkshopServiceById,
    getWorkshopServiceEnum,
    getServiceTypes,
    saveWorkshopService,
} from "../../services/workshopServiceService";
import { getByCurrenyId } from "../../services/currecyService";
import Loading from "../../components/loading";
import ComboboxComponent from "../../components/Combobox";

const AddWorkshopService = () => {
    const { id } = useParams();
    const { token } = useSelector((state) => state.user);
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(false);
    const [calculationTypes, setCalculationTypes] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [serviceTypes, setServiceTypes] = useState([]);
    const [formData, setFormData] = useState({
        id: null,
        name: "",
        calculationType: {},
        price: "",
        currencyId: null,
        serviceType: null,
    });

    const fetchEnums = async () => {
        const response = await getWorkshopServiceEnum(
            `${process.env.REACT_APP_HOST_BASE_URL}/v1/workshop-service/calculation-type`,
            token
        );
        if (response.success) {
            setCalculationTypes(response.data.map((item) => {
                return {
                    ...item,
                    name: item.value,
                }
            }));
        }
    };

    const fetchServiceTypes = async () => {
        const response = await getServiceTypes(
            `${process.env.REACT_APP_HOST_BASE_URL}/v1/workshop-service/service-type`,
            token
        );
        if (response.success) {
            setServiceTypes(response.data);
        }
    };

    const fetchCurrencies = async () => {
        const response = await getByCurrenyId(
            `${process.env.REACT_APP_HOST_BASE_URL}/v1/currency/list`,
            token
        );
        if (response.success) {
            setCurrencies(response.data);
        }
    };

    const fetchById = async () => {
        const response = await getWorkshopServiceById(
            `${process.env.REACT_APP_HOST_BASE_URL}/v1/workshop-service/${id}`,
            token
        );
        if (response.success) {
            const item = response.data;
            setFormData({
                id: item.id,
                name: item.name || "",
                calculationType: item.calculationType ? item.calculationType : {},
                price: item.price || "",
                currencyId: item.currency ? item.currency.id : null,
                serviceType: item.serviceType ? item.serviceType : null,
            });
            console.log("item", item, formData);
        } else {
            navigate("/error", {
                state: {
                    status: response.status,
                    message: response.message ? response.message : null,
                },
            });
        }
    };

    useEffect(() => {
        fetchEnums();
        fetchCurrencies();
        fetchServiceTypes();
        if (id) {
            fetchById();
        }
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleCalculationTypeChange = (e) => {
        setFormData((prev) => ({ ...prev, calculationType: {key:e.key}}));
    };

    const handleServiceTypeChange = (e) => {
        console.log(e);
        setFormData((prev) => ({ ...prev, serviceType: e.id }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const body = {
            id: formData.id,
            name: formData.name.trim(),
            calculationType: formData.calculationType || null,
            price: formData.price !== "" ? parseFloat(formData.price) : null,
            currencyId: formData.currencyId ? parseInt(formData.currencyId) : null,
            serviceType: formData.serviceType ? formData.serviceType : null,
        };
        const response = await saveWorkshopService(
            `${process.env.REACT_APP_HOST_BASE_URL}/v1/workshop-service`,
            token,
            body
        );
        if (response.success) {
            setIsLoading(false);
            navigate("/workshop-service");
            toast.success(id ? t("action.edit_success") : t("action.add_success"), {
                position: "bottom-right",
                autoClose: 3000,
                closeOnClick: true,
                className: "add-success",
            });
        } else {
            setIsLoading(false);
            navigate("/error", {
                state: {
                    status: response.status,
                    message: response.message ? response.message : null,
                },
            });
        }
    };

    if (isLoading) return <Loading />;

    return (
        <div className="px-4 py-4 sm:px-6 lg:px-8">
            <div className="mb-4">
                <h1 className="text-xl font-semibold text-gray-900">
                    {id ? t("update_workshop_service") : t("add_workshop_service")}
                </h1>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4 max-w-lg">
                <div>
                    <label className="block text-sm font-medium text-gray-700">
                        {t("name")}
                    </label>
                    <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2"
                    />
                </div>

                <div>

                    <ComboboxComponent
                      data={calculationTypes}
                      item={
                          formData.calculationType
                            ? calculationTypes.find((item) => item.key === formData.calculationType.key)
                            : null
                      }
                      label={t(`workshop_service_calculation_type`)}
                      onChange={handleCalculationTypeChange}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">
                        {t("price")}
                    </label>
                    <input
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">
                        {t("currency")}
                    </label>
                    <select
                        name="currencyId"
                        value={formData.currencyId || ""}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2"
                    >
                        <option value="">{t("action.select")}</option>
                        {currencies.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name} ({c.symbol})
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <ComboboxComponent
                      data={serviceTypes}
                      item={
                          formData.serviceType
                            ? serviceTypes.find((item) => item.id === formData.serviceType)
                            : null
                      }
                      label={t(`service_type`)}
                      onChange={handleServiceTypeChange}
                    />
                </div>

                <div className="flex gap-3 pt-2">
                    <button
                        type="button"
                        onClick={() => navigate("/workshop-service")}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                        {t("backward")}
                    </button>
                    <button
                        type="submit"
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                    >
                        {t("save")}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddWorkshopService;
